import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ActivityGqlOperation, ApiBaseState, CalendarRequestProps, GqlApiResponse } from 'store/api/api.types';
import { updateApiBaseState } from 'store/api/apiUtils';
import queryActivitiesGqlApi, { resetStateWithDefaultApiBaseState } from 'store/api/activitiesGqlApi';
import { DefaultApiBaseState } from 'store/api/ApiConstants';
import { CalendarResponse } from 'gql/autogen/CalendarResponse';
import { measureCalendarApiResponse } from 'store/api/measurements/calendarGqlMeasurement';
import { getDateInYmdFormat, getTodayDate } from 'core-framework/util/datetime/date';
import { getElementAtIndex } from 'core-framework/util/CommonUtils';
import { parseISO, set } from 'date-fns';
import { AvailabilitySchedule } from 'gql/autogen/AvailabilitySchedule';
import { OfferLevelSchedules } from 'component/page/detail/store/calendar.types';

export interface CalendarState extends ApiBaseState {
    calendarResponse?: CalendarResponse;
    isAdditionalCalendarLoading?: boolean;
    fetchedMonths?: Date[];
    offerLevelCalendars: OfferLevelSchedules;
}

export const calendarInitialState: CalendarState = {
    ...DefaultApiBaseState,
    offerLevelCalendars: {},
};

let calendarInitialStateLazy: CalendarState;

export function getMergedSchedules(newSchedules: AvailabilitySchedule[], oldSchedules: AvailabilitySchedule[]) {
    const oldSchedulesByDate = oldSchedules.reduce<Record<string, AvailabilitySchedule>>((acc, schedule) => {
        if (schedule && schedule.date) {
            acc[schedule.date] = schedule;
        }
        return acc;
    }, {});

    for (const newSchedule of newSchedules) {
        const newScheduleDate = newSchedule?.date;
        if (newScheduleDate) {
            // This line will
            // 1. Replace old records when the new schedule date is the same as the old schedule date
            // 2. add new records when the new schedule date is not found in the old schedules
            oldSchedulesByDate[newScheduleDate] = newSchedule;
        }
    }

    return Object.values(oldSchedulesByDate);
}

const calendarSlice = createSlice({
    name: 'calendar',
    initialState: (): CalendarState => {
        // Intentional: Somehow webpack is unable to load variable DefaultApiBaseState in this file. This implementation is a lazy initialization implementation.
        if (!calendarInitialStateLazy) {
            calendarInitialStateLazy = { ...calendarInitialState, ...DefaultApiBaseState };
        }
        return calendarInitialStateLazy;
    },
    reducers: {
        resetCalendarState: (state) => {
            resetStateWithDefaultApiBaseState(state);
            state.calendarResponse = undefined;
        },
        calendarLoadFinished: (state, action: PayloadAction<GqlApiResponse<CalendarResponse, CalendarRequestProps>>) => {
            const isAdditionalCalendar = action.payload.props.isAdditionalCalendar;
            state.isAdditionalCalendarLoading = action.payload.baseState.isLoading;
            const calendarFromResponse = action.payload.result?.result?.calendar;
            const newSchedules = calendarFromResponse?.schedules ?? [];
            const offersCalendars = calendarFromResponse?.offersCalendar ?? [];
            const firstSchedule = getElementAtIndex(newSchedules, 0);
            const calendarFromState = state.calendarResponse?.result?.calendar;

            offersCalendars.forEach((offerCalendar) => {
                const offerId = offerCalendar.offerId;
                if (!offerId) return;
                state.offerLevelCalendars[offerId] = state.offerLevelCalendars[offerId] || {};
                offerCalendar.schedules?.forEach((schedule) => {
                    if (schedule.date) {
                        state.offerLevelCalendars[offerId][schedule.date] = schedule;
                    }
                });
            });

            if (!isAdditionalCalendar) {
                state.calendarResponse = action.payload.result;
                state.fetchedMonths = firstSchedule?.date ? [parseISO(firstSchedule.date)] : [];
                updateApiBaseState(state, action);
            } else {
                const mergedSchedules = getMergedSchedules(newSchedules, calendarFromState?.schedules || []);

                if (calendarFromState) {
                    calendarFromState.schedules = mergedSchedules;
                }

                const fetchedMonth = firstSchedule && firstSchedule.date ? parseISO(firstSchedule.date) : undefined;
                if (fetchedMonth) {
                    state.fetchedMonths = [...(state.fetchedMonths || []), fetchedMonth];
                }
            }

            if (state.isError || state.isSuccess) {
                measureCalendarApiResponse(state.isSuccess, action.payload.pageTypeId, action.payload.config, state.error, calendarFromResponse);
            }
        },
    },
});

export const fetchCalendarPrice = (date: Date, activityToken: string) => {
    const todayDate = getTodayDate();
    const startDate = set(new Date(), { year: date.getFullYear(), month: date.getMonth(), date: 1 });
    const endDate = set(new Date(), { year: date.getFullYear(), month: date.getMonth() + 1, date: 0 });

    const validStartDate = startDate >= todayDate ? startDate : todayDate;
    const validEndDate = endDate < validStartDate ? validStartDate : endDate;

    const args: CalendarRequestProps = {
        activityToken,
        startDate: getDateInYmdFormat(validStartDate),
        endDate: getDateInYmdFormat(validEndDate),
        isAdditionalCalendar: true,
    };

    queryActivitiesGqlApi({
        operation: ActivityGqlOperation.calendar,
        args: { props: args },
    });
};

export const { resetCalendarState, calendarLoadFinished } = calendarSlice.actions;
export default calendarSlice.reducer;
